// Types shared by the HTTP/WebSocket API and the dashboard.
import type { Alert } from "./alerts.js";
import type { PatternsStatus } from "./patterns.js";
import type { PowerStatus } from "./power.js";
import type { StorageTotals } from "./storage.js";
import type { Stack, StackType } from "./stack.js";

export interface Rule {
  id: number;
  key: string;
  stack: Stack;
  min: number;
  target: number;
  batch: number;
  priority: number;
  enabled: boolean;
  critical: boolean;
  /** CPU names this rule may use. Empty means any CPU. */
  cpus: string[];
  /** The group that made this rule and keeps it in line, or null for a rule of its own. */
  groupId: number | null;
  createdAt: number;
  updatedAt: number;
}

export type RuleInput = Pick<Rule, "stack" | "min" | "target" | "batch"> &
  Partial<Pick<Rule, "priority" | "enabled" | "critical" | "cpus">>;

export type RuleStatus = "ok" | "low" | "crafting" | "backoff" | "disabled" | "unknown";

/** A rule with its live state, as the Stock page shows it. */
export interface RuleView extends Rule {
  /** Name of the group in `groupId`. */
  groupName: string | null;
  stock: number | null;
  status: RuleStatus;
  lastFailure: { reason: string; at: number } | null;
  backoffUntil: number | null;
}

/**
 * Which craftables a group covers. Every field that is set must match; a
 * group with no field set is refused, so "everything" is never one click away.
 */
export interface GroupMatch {
  type?: StackType;
  /** Ore dictionary names, comma separated, `*` as a wildcard (e.g. "circuit*"). Any one must match. */
  ore?: string;
  /** Words that must all appear in the label, key or ore names, like the craftables search. */
  text?: string;
}

/**
 * One rule for many stacks ("all essentia at 5000"). The group keeps a real
 * rule for every craftable it matches, so planning, watching and offline
 * stocking treat them like any other rule. A stack with a rule of its own is
 * left alone, and editing a group's rule turns it into a rule of its own.
 */
export interface RuleGroup {
  id: number;
  name: string;
  match: GroupMatch;
  min: number;
  target: number;
  batch: number;
  priority: number;
  enabled: boolean;
  critical: boolean;
  cpus: string[];
  /** Keys the group skips, e.g. a group rule someone deleted. */
  exclude: string[];
  createdAt: number;
  updatedAt: number;
}

export type RuleGroupInput = Pick<RuleGroup, "name" | "match" | "min" | "target" | "batch"> &
  Partial<Pick<RuleGroup, "priority" | "enabled" | "critical" | "cpus" | "exclude">>;

/** A group with its rules' live state, as the Groups page shows it. */
export interface RuleGroupView extends RuleGroup {
  members: number;
  low: number;
  crafting: number;
}

/** How a group's matcher applies to one craftable, for the preview before saving. */
export interface GroupPreviewItem {
  stack: Stack;
  key: string;
  hint: string | null;
  /** member: the group stocks it. own: it has a rule of its own. other: another group has it. excluded: skipped on purpose. */
  state: "member" | "own" | "other" | "excluded";
  /** Name of the other group when state is "other". */
  group?: string;
}

export interface GroupPreview {
  items: GroupPreviewItem[];
  /** Matches in total; items holds the first few hundred. */
  matches: number;
  members: number;
  /** Craftables known to the app. 0 means they were never loaded. */
  craftables: number;
}

export interface Settings {
  /** CPUs the decision loop always leaves free for manual crafts. */
  reservedCpus: number;
  /** Max critical rules copied to the computer's disk for offline stocking. */
  maxCriticalRules: number;
  /** Seconds between decision passes (a stock change also triggers one). */
  decisionInterval: number;
  /** Turns automatic crafting off without disabling every rule. */
  autoCraft: boolean;
  prioritizePower: boolean;
  /** Puts everything the network stores on the home page, read every few minutes. Never crafts. */
  monitorAll: boolean;
  /** Days every single stock change is kept (hourly totals are kept a year). */
  flowDays: number;
}

export interface StackAmount {
  stack: Stack;
  amount: number;
}

export interface CpuInfo {
  name: string;
  /** 1-based position in getCpus(), stable while the network is unchanged. */
  index: number | null;
  storage: number;
  coprocessors: number;
  busy: boolean;
  output: StackAmount | null;
  active: StackAmount[];
  pending: StackAmount[];
  /** Job id from the app when the app knows which job runs here. */
  job: string | null;
}

export type JobState = "requested" | "computing" | "running" | "done" | "failed" | "canceled";
export type JobOrigin = "auto" | "manual" | "adopted";

export interface Job {
  id: string;
  key: string;
  stack: Stack;
  amount: number;
  cpu: string | null;
  state: JobState;
  reason: string | null;
  origin: JobOrigin;
  ruleId: number | null;
  createdAt: number;
  updatedAt: number;
}

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  id: number;
  at: number;
  level: LogLevel;
  source: "app" | "game";
  msg: string;
}

export interface PowerInfo {
  stored: number;
  max: number;
  usage: number;
  injection: number;
  idle: number;
  at: number;
}

export interface LinkInfo {
  connected: boolean;
  butler: string | null;
  version: string | null;
  since: number | null;
  remote: string | null;
}

export interface Snapshot {
  link: LinkInfo;
  cpus: CpuInfo[];
  jobs: Job[];
  power: PowerInfo | null;
  settings: Settings;
  ruleCount: number;
  craftablesCount: number;
  /** Craftables that came with ore dictionary names. 0 means the Butler is too old or OpenComputers has insertIdsInConverters off. */
  craftablesWithOre: number;
  craftablesAt: number | null;
  /** Set while a craftables fetch from the game is running. */
  craftablesLoading: CraftablesLoading | null;
  /** Alerts not dismissed yet, newest first. */
  alerts: Alert[];
  /** What the app knows of the network's contents, for the resource picker. */
  network: NetworkStatus;
  /** Keys of the favorite stacks, pinned to the top of every list. */
  favorites: string[];
}

/** The last read of everything stored in the network (Butler 0.4.0 and later). */
export interface NetworkStatus {
  /** Stacks stored at the last read. 0 means never read. */
  count: number;
  at: number | null;
  /** Set while a read is running. */
  loading: { startedAt: number; received: number; progress: number | null } | null;
}

/** A resource monitored on the home page without a rule: it is watched and charted, never crafted. */
export interface TrackedResource {
  key: string;
  stack: Stack;
  createdAt: number;
}

/** Progress of a running craftables fetch. */
export interface CraftablesLoading {
  startedAt: number;
  /** Stacks received so far. */
  received: number;
  /** Stacks the game will send, when it says (newer Butlers do). */
  total: number | null;
}

/** Messages the server pushes to dashboards over the WebSocket. */
export type LiveEvent =
  | { t: "snapshot"; data: Snapshot }
  | { t: "link"; data: LinkInfo }
  | { t: "cpus"; data: CpuInfo[] }
  | { t: "job"; data: Job }
  | { t: "power"; data: PowerInfo }
  | { t: "log"; data: LogEntry }
  | { t: "stock"; data: Array<[string, number]> }
  | { t: "rules"; data: { changed: number[]; removed: number[]; count: number } }
  | { t: "groups"; data: { changed: number[]; removed: number[] } }
  | { t: "settings"; data: Settings }
  | { t: "craftables"; data: { count: number; withOre: number; at: number } }
  | { t: "craftables.progress"; data: CraftablesLoading | null }
  | { t: "alert"; data: Alert }
  | { t: "alerts.dismissed"; data: { ids: number[] } }
  | { t: "patterns"; data: PatternsStatus }
  | { t: "network"; data: NetworkStatus }
  | { t: "tracked"; data: { count: number } }
  | { t: "favorites"; data: { keys: string[] } }
  | { t: "machines"; data: PowerStatus }
  | { t: "storage"; data: { at: number | null; totals: StorageTotals | null; error: string | null } };

export interface Page<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

/** Windows the rates and charts can be read over. */
export const HISTORY_RANGES = { "1h": 3_600_000, "6h": 21_600_000, "24h": 86_400_000, "7d": 604_800_000, "30d": 2_592_000_000 } as const;
export type HistoryRange = keyof typeof HISTORY_RANGES;

/** How fast a stack goes up and down, from its stock samples. Per hour. */
export interface StockRates {
  /** Sum of increases (crafts, auto-production) per hour. */
  prodPerHour: number;
  /** Sum of decreases per hour, as a positive number. */
  usePerHour: number;
  netPerHour: number;
  /** Hours the samples cover; rates over less than a few minutes are null. */
  coveredHours: number;
  /** When stock reaches 0 at the current net rate. Null unless it is falling. */
  emptyAt: number | null;
  /** When stock drops below min at the current net rate. Null unless it is falling and still above min. */
  belowMinAt: number | null;
}

/** "tracked": monitored without a rule, so never crafted. */
export type ResourceStatus = RuleStatus | "tracked";

/** One monitored resource as the home page lists it: a rule, or a tracked resource. */
export interface ResourceStat {
  /** Null for a tracked resource with no rule. */
  ruleId: number | null;
  /** Monitored on its own (it stays on the home page without a rule). */
  tracked: boolean;
  /** Pinned to the top; a favorite stays on the home page without a rule. */
  favorite: boolean;
  key: string;
  stack: Stack;
  stock: number | null;
  /** 0 without a rule. */
  min: number;
  target: number;
  status: ResourceStatus;
  rates: StockRates | null;
}

export interface CpuStat {
  name: string;
  busy: boolean;
  output: StackAmount | null;
  /** When the current craft started, as far as the app saw it. */
  busySince: number | null;
  /** Finished crafts over the last 7 days. */
  crafts: number;
  /** Average craft time in ms over those crafts. */
  avgCraftMs: number | null;
  /** Share of the last 24 h the CPU was busy, 0 to 1. */
  busyShare24h: number;
  lastUsedAt: number | null;
}

export interface Overview {
  range: HistoryRange;
  resources: ResourceStat[];
  cpus: CpuStat[];
}

export interface StockHistory {
  ruleId: number | null;
  tracked: boolean;
  favorite: boolean;
  key: string;
  stack: Stack;
  stock: number | null;
  min: number;
  target: number;
  range: HistoryRange;
  /** [time, amount], oldest first. Stock stays at a point's amount until the next point. */
  points: Array<[number, number]>;
  rates: StockRates | null;
}

/** Windows the per-change production and use figures are given over. */
export const FLOW_WINDOWS = { "1h": 3_600_000, "24h": 86_400_000, "7d": 604_800_000, "30d": 2_592_000_000, "1y": 31_536_000_000 } as const;
export type FlowWindow = keyof typeof FLOW_WINDOWS;

/** Every rise and fall of one stack over a window, from each change the Butler reported. */
export interface FlowWindowStat {
  window: FlowWindow;
  /** Sum of every rise (crafts, auto-production, inserts). */
  in: number;
  /** Sum of every fall, as a positive number. */
  out: number;
  /** Changes counted. */
  moves: number;
  /** Time inside the window the app was watching the stack live; averages divide by this. */
  observedMs: number;
  /** False when the window is longer than every change is kept, so it is read from hourly totals and starts on the hour. */
  exact: boolean;
}

/** One stock change as the Butler reported it. */
export interface FlowMove {
  at: number;
  /** Change from the amount before. */
  delta: number;
  /** Amount after the change. */
  amount: number;
}

export interface ResourceFlows {
  key: string;
  stack: Stack;
  stock: number | null;
  /** Whether the Butler watches it now, so every change is recorded. */
  watched: boolean;
  windows: FlowWindowStat[];
  /** Newest first. */
  moves: FlowMove[];
  /** Days every change is kept (the flowDays setting). */
  keptDays: number;
}

/** One row of the Flows page. */
export interface FlowRow {
  key: string;
  stack: Stack;
  stock: number | null;
  ruleId: number | null;
  favorite: boolean;
  watched: boolean;
  in: number;
  out: number;
  moves: number;
  observedMs: number;
}

export interface FlowTable {
  window: FlowWindow;
  exact: boolean;
  keptDays: number;
  rows: FlowRow[];
}

/** Spans the in/out chart covers, each with its bucket size. */
export const FLOW_SERIES = {
  "6h": { spanMs: 21_600_000, bucketMs: 300_000 },
  "48h": { spanMs: 172_800_000, bucketMs: 3_600_000 },
  "30d": { spanMs: 2_592_000_000, bucketMs: 3_600_000 },
} as const;
export type FlowSeriesRange = keyof typeof FLOW_SERIES;

/** In and out of one stack per bucket, for the bar chart. */
export interface FlowSeries {
  key: string;
  range: FlowSeriesRange;
  from: number;
  to: number;
  bucketMs: number;
  /** [bucket start, in, out], oldest first; buckets with no change are left out. */
  buckets: Array<[number, number, number]>;
}
